// Simulated end-to-end encryption using Web Crypto API (AES-GCM)
// In a real production system, key derivation implies additional salting and potentially PBKDF2.

const ALGORITHM = 'AES-GCM';
const KEY_LENGTH = 256;

// Derive a symmetric key from the user's UID for local encryption
const deriveKey = async (uid) => {
  const encoder = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(uid),
    { name: 'PBKDF2' },
    false,
    ['deriveBits', 'deriveKey']
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: encoder.encode('devtracker-chatauth-salt'),
      iterations: 100000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: ALGORITHM, length: KEY_LENGTH },
    false,
    ['encrypt', 'decrypt']
  );
};

// Encrypt a plaintext message
export const encryptMessage = async (uid, plainText) => {
  try {
    const key = await deriveKey(uid);
    const encoder = new TextEncoder();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encryptedContent = await window.crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      key,
      encoder.encode(plainText)
    );

    // Combine IV and encrypted buffer into a base64 string for storage
    const encryptedArray = new Uint8Array(encryptedContent);
    const combined = new Uint8Array(iv.length + encryptedArray.length);
    combined.set(iv, 0);
    combined.set(encryptedArray, iv.length);

    return btoa(String.fromCharCode.apply(null, combined));
  } catch (err) {
    console.error("Encryption failed:", err);
    return null;
  }
};

// Decrypt a base64 encrypted payload
export const decryptMessage = async (uid, encryptedBase64) => {
  try {
    const key = await deriveKey(uid);
    const combinedStream = atob(encryptedBase64);
    const combined = new Uint8Array(combinedStream.length);
    for (let i = 0; i < combinedStream.length; i++) {
      combined[i] = combinedStream.charCodeAt(i);
    }

    const iv = combined.slice(0, 12);
    const encryptedData = combined.slice(12);

    const decryptedContent = await window.crypto.subtle.decrypt(
      { name: ALGORITHM, iv },
      key,
      encryptedData
    );

    const decoder = new TextDecoder();
    return decoder.decode(decryptedContent);
  } catch (err) {
    console.error("Decryption failed:", err);
    return "[Encrypted/Unreadable]";
  }
};

// --- BHARATVERIFY NEURAL AUTH (TOTP) IMPLEMENTATION ---

// Helper: Base32 decoding for Google Authenticator secrets
const base32ToBuffer = (base32) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  const clean = base32.toUpperCase().replace(/[\s-]/g, '');
  let bits = '';
  for (let i = 0; i < clean.length; i++) {
    const val = alphabet.indexOf(clean[i]);
    if (val === -1) continue;
    bits += val.toString(2).padStart(5, '0');
  }
  const bytes = [];
  for (let i = 0; i + 8 <= bits.length; i += 8) {
    bytes.push(parseInt(bits.substring(i, i + 8), 2));
  }
  return new Uint8Array(bytes);
};

// Helper: Integer to 8-byte buffer (big-endian)
const intToBuffer = (num) => {
  const buf = new Uint8Array(8);
  for (let i = 7; i >= 0; i--) {
    // Correctly handle 64-bit integer into 8 bytes
    // Since we only care about timeStep, 32-bit is enough for 136 years but let's be safe
    const byte = Math.floor(num / Math.pow(256, i));
    buf[7 - i] = byte & 0xff;
  }
  return buf;
};

// Generate TOTP Code (6-digit)
export const generateTOTP = async (secret) => {
  try {
    const keyBuffer = base32ToBuffer(secret);
    const epoch = Math.floor(Date.now() / 1000);
    const timeStep = Math.floor(epoch / 30);
    const timeBuffer = new Uint8Array(8);
    // Fill time buffer (big-endian)
    let temp = timeStep;
    for (let i = 7; i >= 0; i--) {
      timeBuffer[i] = temp & 0xff;
      temp = Math.floor(temp / 256);
    }

    const key = await window.crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );

    const hmac = await window.crypto.subtle.sign('HMAC', key, timeBuffer);
    const hash = new Uint8Array(hmac);
    const offset = hash[hash.length - 1] & 0xf;
    
    let binary = ((hash[offset] & 0x7f) << 24) |
                 ((hash[offset + 1] & 0xff) << 16) |
                 ((hash[offset + 2] & 0xff) << 8) |
                 (hash[offset + 3] & 0xff);

    const otp = (binary % 1000000).toString().padStart(6, '0');
    return otp;
  } catch (err) {
    console.error("TOTP generation failed", err);
    return null;
  }
};

// Verify TOTP Code (with window for drift)
export const verifyTOTP = async (secret, token) => {
  if (!secret || !token) return false;
  try {
    const keyBuffer = base32ToBuffer(secret);
    const key = await window.crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );

    // Verify current, previous, and next time steps for clock drift (±2 steps = ±60s)
    for (let i = -2; i <= 2; i++) {
        const epoch = Math.floor(Date.now() / 1000) + (i * 30);
        const timeStep = Math.floor(epoch / 30);
        
        // Use 8-byte big-endian time buffer as per RFC 6238
        const timeBuffer = new Uint8Array(8);
        const view = new DataView(timeBuffer.buffer);
        // Set only the lower 4 bytes for modern timestamps (32-bit Unix epoch is enough until 2038+)
        // But the 8-byte buffer MUST be big-endian for TOTP
        view.setUint32(4, timeStep, false); 
        // If timeStep > 2^32, the high bits (view.setUint32(0, ...)) would need to be handled
        // but for now, 0-3 bytes stay zero, which is correct big-endian for 2024 (timeStep ~57M)
    
        const hmac = await window.crypto.subtle.sign('HMAC', key, timeBuffer);
        const hash = new Uint8Array(hmac);
        const offset = hash[hash.length - 1] & 0xf;
        let binary = ((hash[offset] & 0x7f) << 24) |
                     ((hash[offset + 1] & 0xff) << 16) |
                     ((hash[offset + 2] & 0xff) << 8) |
                     (hash[offset + 3] & 0xff);
    
        if ((binary % 1000000).toString().padStart(6, '0') === token) return true;
    }
  } catch (err) {
    console.error("TOTP verification failed", err);
  }
  return false;
};

// Generate a random Base32 secret for setup
export const generateVerificationSecret = () => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let secret = '';
  const randomValues = window.crypto.getRandomValues(new Uint8Array(20));
  for (let i = 0; i < randomValues.length; i++) {
    secret += alphabet[randomValues[i] % alphabet.length];
  }
  return secret;
};
